import {useMinSpin} from "../../hooks/useMinSpin";
import {FormEvent, useState} from "react";
import axios from "axios";
import {PATHS_API} from "../../../enum";
import ProductImage from "../../shared/ProductImage/ProductImage";
import ButtonLabel from "../../shared/ButtonLoader/ButtonLoader";
import {IAdminProduct, isImageUrl, itemsToText, IVariant, PRODUCT_TYPES, textToItems} from "./adminTypes";
import ImageUpload from "./ImageUpload";

interface IOption { _id: string, name: string }

interface IVariantDraft {
    _id?: string,
    name: string,
    color: string,
    imageUrl: string,
    className: string,
    itemsText: string,
}

const toDraft = (v: IVariant): IVariantDraft => ({
    _id: v._id,
    name: v.name,
    color: v.color || "#888888",
    imageUrl: v.imageUrl || "",
    className: v.className,
    itemsText: itemsToText(v.items),
});

const EMPTY: Omit<IAdminProduct, "_id"> = {
    name: "", description: "", type: "product", className: "", count: 1, priority: 0,
    imageUrl: "", price: 0, categories: [], servers: [], items: [], variants: [],
};

// Форма створення / редагування товару. Значення ще раз перевіряє сервер (STRICT_VALIDATION + DTO).
const ProductEditor = ({ product, categories, servers, onClose, onSaved }: {
    product: IAdminProduct | null,
    categories: IOption[],
    servers: IOption[],
    onClose: () => void,
    onSaved: (message: string) => void,
}) => {
    const initial = product ?? { ...EMPTY, _id: "" };
    const [form, setForm] = useState({
        name: initial.name,
        description: initial.description ?? "",
        type: initial.type,
        className: initial.className ?? "",
        count: String(initial.count ?? 1),
        priority: String(initial.priority ?? 0),
        imageUrl: initial.imageUrl ?? "",
        price: String(initial.price ?? 0),
        categories: initial.categories ?? [],
        servers: initial.servers ?? [],
        itemsText: itemsToText(initial.items),
    });
    const [variants, setVariants] = useState<IVariantDraft[]>((initial.variants ?? []).map(toDraft));
    const [errors, setErrors] = useState<string[]>([]);
    const [saving, setSaving] = useState(false);
    // спінер видно щонайменше пів секунди (useMinSpin)
    const saveSpin = useMinSpin(saving);

    const set = (key: keyof typeof form, value: any) => setForm((f) => ({ ...f, [key]: value }));
    const toggle = (key: "categories" | "servers", id: string) =>
        set(key, form[key].includes(id) ? form[key].filter((x) => x !== id) : [...form[key], id]);

    const setVariant = (i: number, patch: Partial<IVariantDraft>) =>
        setVariants((vs) => vs.map((v, j) => j === i ? { ...v, ...patch } : v));

    const moveVariant = (i: number, dir: -1 | 1) => setVariants((vs) => {
        const next = [...vs];
        const j = i + dir;
        if (j < 0 || j >= next.length) return vs;
        [next[i], next[j]] = [next[j], next[i]];
        return next;
    });

    const submit = async (e: FormEvent) => {
        e.preventDefault();
        const problems: string[] = [];

        const { items, errors: itemErrors } = textToItems(form.itemsText);
        problems.push(...itemErrors.map((m) => `Предмети: ${m}`));

        const variantPayload = variants.map((v, i) => {
            const parsed = textToItems(v.itemsText);
            problems.push(...parsed.errors.map((m) => `Колір ${i + 1}: ${m}`));
            if (!v.name.trim()) problems.push(`Колір ${i + 1}: вкажіть назву`);
            if (!v.className.trim()) problems.push(`Колір ${i + 1}: вкажіть класнейм`);
            if (v.imageUrl.trim() && !isImageUrl(v.imageUrl.trim())) problems.push(`Колір ${i + 1}: картинка — посилання https:// або завантажений файл`);
            return {
                name: v.name.trim(),
                color: v.color,
                ...(v.imageUrl.trim() ? { imageUrl: v.imageUrl.trim() } : {}),
                className: v.className.trim(),
                ...(parsed.items.length ? { items: parsed.items } : {}),
            };
        });

        if (!form.name.trim()) problems.push("Вкажіть назву");
        if (!isImageUrl(form.imageUrl.trim())) problems.push("Картинка: посилання https:// або завантажте файл");

        if (problems.length) { setErrors(problems); return; }

        const payload = {
            name: form.name.trim(),
            description: form.description,
            type: form.type,
            className: form.className.trim(),
            count: Number(form.count),
            priority: Number(form.priority),
            imageUrl: form.imageUrl.trim(),
            price: Number(form.price),
            categories: form.categories,
            servers: form.servers,
            items,
            variants: variantPayload,
        };

        setSaving(true);
        setErrors([]);
        try {
            if (product) {
                await axios.put(`${PATHS_API.ADMIN_PRODUCTS}/${product._id}`, payload, { withCredentials: true });
                onSaved(`Товар «${payload.name}» збережено`);
            } else {
                await axios.post(PATHS_API.ADMIN_PRODUCTS, payload, { withCredentials: true });
                onSaved(`Товар «${payload.name}» створено`);
            }
        } catch (err: any) {
            const message = err.response?.data?.message;
            setErrors(Array.isArray(message) ? message : [typeof message === "string" ? message : "Не вдалося зберегти товар"]);
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="adm-overlay" onClick={onClose}>
            <form className="adm-editor" onClick={(e) => e.stopPropagation()} onSubmit={submit}>
                <div className="adm-editor-head">
                    <h4>{product ? "Редагування товару" : "Новий товар"}</h4>
                    <button type="button" className="adm-icon-btn" onClick={onClose} aria-label="Закрити">✕</button>
                </div>

                <div className="adm-editor-body">
                    <div className="adm-editor-top">
                        <div className="adm-preview">
                            <ProductImage src={variants[0]?.imageUrl || form.imageUrl} alt="" />
                        </div>
                        <div className="adm-fields">
                            <label className="adm-field adm-span-2">
                                <span>Назва</span>
                                <input value={form.name} onChange={(e) => set("name", e.target.value)} maxLength={80} required />
                            </label>
                            <label className="adm-field">
                                <span>Тип</span>
                                <select value={form.type} onChange={(e) => set("type", e.target.value)}>
                                    { PRODUCT_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>) }
                                </select>
                            </label>
                            <label className="adm-field">
                                <span>Ціна, ₴</span>
                                <input type="number" min={0} step="any" value={form.price} onChange={(e) => set("price", e.target.value)} required />
                            </label>
                            <label className="adm-field">
                                <span>{form.type === "priority" || form.type === "set" ? "Днів" : "Кількість"}</span>
                                <input type="number" min={1} step={1} value={form.count} onChange={(e) => set("count", e.target.value)} required />
                            </label>
                            <label className="adm-field">
                                <span>Порядок (більше — вище)</span>
                                <input type="number" value={form.priority} onChange={(e) => set("priority", e.target.value)} />
                            </label>
                        </div>
                    </div>

                    <div className="adm-field">
                        <span>Картинка: посилання https:// або завантажте файл</span>
                        <div className="adm-input-row">
                            <input value={form.imageUrl} onChange={(e) => set("imageUrl", e.target.value)} placeholder="https://i.imgur.com/....png" required />
                            <ImageUpload onUploaded={(url) => set("imageUrl", url)} onError={(m) => setErrors([m])} />
                        </div>
                    </div>

                    <label className="adm-field">
                        <span>Опис</span>
                        <textarea rows={3} value={form.description} onChange={(e) => set("description", e.target.value)} maxLength={2000} />
                    </label>

                    <div className="adm-grid-2">
                        <label className="adm-field">
                            <span>Класнейм для видачі</span>
                            <input value={form.className} onChange={(e) => set("className", e.target.value)} placeholder="Hatchback_02_Black" />
                        </label>
                        <label className="adm-field">
                            <span>Предмети: «ClassName кількість» у рядку</span>
                            <textarea rows={3} className="adm-mono" value={form.itemsText} onChange={(e) => set("itemsText", e.target.value)} placeholder={"Wrench 1\nHeadlightH7 2"} />
                        </label>
                    </div>

                    <div className="adm-field">
                        <span>Категорії</span>
                        <div className="adm-chips">
                            { categories.map((c) =>
                                <button key={c._id} type="button" className={`adm-chip ${form.categories.includes(c._id) ? "on" : ""}`} onClick={() => toggle("categories", c._id)}>{c.name}</button>
                            )}
                        </div>
                    </div>

                    <div className="adm-field">
                        <span>Сервери (зніміть галочку, де товар НЕ продається)</span>
                        <div className="adm-chips">
                            { servers.map((s) => {
                                const sold = !form.servers.includes(s._id);
                                return <button key={s._id} type="button" className={`adm-chip ${sold ? "on" : "off"}`} onClick={() => toggle("servers", s._id)}>{sold ? "✓ " : "✕ "}{s.name}</button>;
                            })}
                        </div>
                    </div>

                    <div className="adm-variants">
                        <div className="adm-variants-head">
                            <span>Кольори {variants.length > 0 && <em>— перший показується за замовчуванням</em>}</span>
                            <button type="button" className="adm-ghost-btn" onClick={() => setVariants((vs) => [...vs, { name: "", color: "#888888", imageUrl: "", className: form.className, itemsText: form.itemsText }])}>+ Колір</button>
                        </div>
                        { variants.length === 0 && <p className="adm-muted">Без кольорів: гравець купує сам товар. Додайте кольори, щоб гравець міг обрати.</p> }
                        { variants.map((v, i) =>
                            <div key={i} className="adm-variant">
                                <div className="adm-variant-row">
                                    <input type="color" value={v.color} onChange={(e) => setVariant(i, { color: e.target.value })} aria-label="Колір" />
                                    <input value={v.name} onChange={(e) => setVariant(i, { name: e.target.value })} placeholder="Назва кольору" maxLength={40} />
                                    <input value={v.className} onChange={(e) => setVariant(i, { className: e.target.value })} placeholder="Класнейм" />
                                    <div className="adm-variant-actions">
                                        <button type="button" className="adm-icon-btn" onClick={() => moveVariant(i, -1)} disabled={i === 0} aria-label="Вище">↑</button>
                                        <button type="button" className="adm-icon-btn" onClick={() => moveVariant(i, 1)} disabled={i === variants.length - 1} aria-label="Нижче">↓</button>
                                        <button type="button" className="adm-icon-btn adm-danger" onClick={() => setVariants((vs) => vs.filter((_, j) => j !== i))} aria-label="Прибрати колір">✕</button>
                                    </div>
                                </div>
                                <div className="adm-variant-row">
                                    <ProductImage className="adm-variant-img" src={v.imageUrl || form.imageUrl} alt="" />
                                    <input value={v.imageUrl} onChange={(e) => setVariant(i, { imageUrl: e.target.value })} placeholder="Картинка кольору, порожньо — картинка товару" />
                                    <ImageUpload onUploaded={(url) => setVariant(i, { imageUrl: url })} onError={(m) => setErrors([m])} />
                                </div>
                                <textarea rows={2} className="adm-mono" value={v.itemsText} onChange={(e) => setVariant(i, { itemsText: e.target.value })} placeholder="Деталі цього кольору (порожньо — предмети товару)" />
                            </div>
                        )}
                    </div>

                    { errors.length > 0 &&
                        <ul className="adm-errors">{ errors.map((m, i) => <li key={i}>{m}</li>) }</ul>
                    }
                </div>

                <div className="adm-editor-foot">
                    <button type="button" className="adm-ghost-btn" onClick={onClose}>Скасувати</button>
                    <button type="submit" className={`adm-primary-btn ${saveSpin ? "btn-loading" : ""}`} disabled={saveSpin}>
                        <ButtonLabel loading={saveSpin} loadingText="Зберігаємо...">{product ? "Зберегти" : "Створити"}</ButtonLabel>
                    </button>
                </div>
            </form>
        </div>
    );
};

export default ProductEditor;
